import { Card, Colors, NonIdealState } from "@blueprintjs/core";
import * as React from "react";
import { useQuery } from "react-query";
import styled from "styled-components";
import { ERoutes } from "../../AppRouter";

import UserIcon from "../../components/UserIcon";
import { useCommonHooks } from "../../hooks";
import { ETLCodes } from "../../locales";
import TeamPlaceholder from "./TeamPlaceholder";

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;

  & .card-title {
    font-weight: 600;
    font-size: large;
    display: flex;
    align-items: center;
    color: ${Colors.DARK_GRAY3};

    & > * + * {
      margin-left: 0.5rem;
    }
  }

  /* ===== Scrollbar CSS ===== */
  /* Chrome, Edge, and Safari */
  & *::-webkit-scrollbar {
    width: 10px;
  }

  & *::-webkit-scrollbar-track {
    background: #ffffff;
  }

  & *::-webkit-scrollbar-thumb {
    background-color: #c1c1c1;
    border-radius: 10px;
    border: 3px solid #ffffff;
  }
`;

const MembersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex: 1;
`;

export const MemberContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 0.5rem;
  padding: 10px;
  width: 22%;
  margin-left: 2rem;

  &.interactive:hover {
    cursor: pointer;
    background-color: ${Colors.LIGHT_GRAY5};
    border-radius: 5px;
  }

  @media (max-width: 1200px) {
    width: 30%;
  }
  @media (max-width: 1000px) {
    width: 47%;
  }
  & > span {
    color: ${Colors.DARK_GRAY3};
    font-weight: 500;
    font-size: 12pt;
  }
`;

export interface IHomeTeamProps {
  profileMode?: boolean;
}

export const HomeTeam: React.FunctionComponent<IHomeTeamProps> = ({
  profileMode,
}) => {
  const { t, user, apis } = useCommonHooks();

  const { data, isFetching, isFetched } = useQuery(
    ["user-team", user?.userId],
    async () => {
      return await apis.user.azureTeam();
    }
  );

  const hasData = React.useMemo(
    () => !!data && data.length > 0 && isFetched,
    [data, isFetched]
  );

  const membersDisplayer = React.useMemo(
    () =>
      hasData ? (
        data?.map((item) => (
          <MemberContainer
            className={!!item.smileUserId && "interactive"}
            onClick={() =>
              !!item.smileUserId &&
              window.open(`#${ERoutes.suivis}?iduser=${item.smileUserId}`)
            }
          >
            <UserIcon
              azureId={item.o365UserId}
              initials={
                (!!item.lastName ? item.lastName[0] : "") + item.firstName[0]
              }
              size={50}
              fontSize={20}
            />
            <span>{item.displayName}</span>
          </MemberContainer>
        ))
      ) : (
        <NonIdealState
          title={t(ETLCodes.NoMembersFound)}
          icon="blocked-person"
        />
      ),
    [data, hasData, t]
  );

  return !profileMode ? (
    <StyledCard>
      <div className="card-title">{t(ETLCodes.MyTeam)}</div>
      <MembersContainer>
        {isFetching ? <TeamPlaceholder /> : membersDisplayer}
      </MembersContainer>
    </StyledCard>
  ) : (
    <MembersContainer>
      {isFetching ? <TeamPlaceholder /> : membersDisplayer}
    </MembersContainer>
  );
};

export default HomeTeam;
